import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { usePageReader } from '../hooks/usePageReader';
import './AccessibilityBar.css';

const AccessibilityBar = () => {
  const { t, isMarathi, toggleLanguage } = useLanguage();
  const { toggleTheme, isGrayscale } = useTheme();
  const { 
    increaseFontSize, 
    decreaseFontSize, 
    resetFontSize, 
    isMinFontSize, 
    isMaxFontSize 
  } = useAccessibility();
  
  const { isReading, toggleReading } = usePageReader();

  const scrollToMainContent = () => {
    const newsTicker = document.getElementById('news-ticker');
    if (newsTicker) {
      newsTicker.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleScreenReaderClick = () => {
    toggleReading();
  };

  return (
    <div className="accessibility-bar">
      <div className="accessibility-container">
        <div className="accessibility-content">
          {/* Skip to Main Content Button */}
          <button
            className="skip-to-main-content"
            onClick={scrollToMainContent}
            aria-label={isMarathi() ? 'मुख्य मजकुरावर जा' : 'Skip to main content'}
          >
            {isMarathi() ? 'मुख्य मजकुरावर जा' : 'Skip to main content'}
          </button>
          
          {/* Font Size Controls */}
          <div className="font-controls">
            <button
              className="accessibility-btn font-btn"
              onClick={decreaseFontSize}
              disabled={isMinFontSize()}
              title={t('decreaseFont')}
              aria-label={t('decreaseFont')}
            >
              A-
            </button>
            <button
              className="accessibility-btn font-btn"
              onClick={resetFontSize}
              title={t('resetFont')}
              aria-label={t('resetFont')}
            >
              A
            </button>
            <button
              className="accessibility-btn font-btn"
              onClick={increaseFontSize}
              disabled={isMaxFontSize()}
              title={t('increaseFont')}
              aria-label={t('increaseFont')}
            >
              A+
            </button>
          </div>

          {/* Theme Toggle */}
          <button
            className="accessibility-btn theme-toggle"
            onClick={toggleTheme}
            title={isGrayscale() ? t('defaultTheme') : t('grayscaleTheme')}
            aria-label={isGrayscale() ? t('defaultTheme') : t('grayscaleTheme')}
          >
          </button>

          {/* Screen Reader Button with SVG Icon */}
          <button
            type="button"
            className={`accessibility-btn screen-reader-toggle ${isReading ? 'reading' : ''}`}
            onClick={handleScreenReaderClick}
            aria-pressed={isReading}
            title={
              isReading
                ? isMarathi()
                  ? 'वाचन थांबवा'
                  : 'Stop reading'
                : isMarathi()
                ? 'हे पृष्ठ आवाजात वाचा'
                : 'Read this page aloud'
            }
            aria-label={
              isReading
                ? isMarathi()
                  ? 'वाचन थांबवा'
                  : 'Stop reading'
                : isMarathi()
                ? 'हे पृष्ठ आवाजात वाचा'
                : 'Read this page aloud'
            }
          >
            {isReading ? (
              // Stop icon when reading
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="6" y="6" width="12" height="12" rx="2"/>
              </svg>
            ) : (
              // Speaker/Play icon when not reading
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            )}
          </button>

          {/* Language Toggle */}
          <button
            className="accessibility-btn language-toggle"
            onClick={toggleLanguage}
            title={t('language')}
            aria-label={t('language')}
          >
            {isMarathi() ? 'English' : 'मराठी'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityBar;
